'use client';

import { cn } from '@/lib/utils';
import { Delete } from 'lucide-react';

interface NumberPadProps {
  value: string;
  onChange: (value: string) => void;
  onEnter?: () => void;
  maxLength?: number;
  className?: string;
}

export function NumberPad({
  value,
  onChange,
  onEnter,
  maxLength = 10,
  className,
}: NumberPadProps) {
  const handleKeyPress = (key: string) => {
    if (key === 'backspace') {
      onChange(value.slice(0, -1));
    } else if (key === 'clear') {
      onChange('');
    } else if (key === 'enter') {
      onEnter?.();
    } else if (key === '00') {
      if (value.length + 2 <= maxLength) {
        onChange(value + '00');
      }
    } else if (key === '.') {
      if (!value.includes('.')) {
        onChange(value + '.');
      }
    } else {
      if (value.length < maxLength) {
        onChange(value + key);
      }
    }
  };

  const buttons = [
    ['7', '8', '9'],
    ['4', '5', '6'],
    ['1', '2', '3'],
    ['00', '0', '.'],
  ];

  return (
    <div className={cn('grid gap-2', className)}>
      {/* 숫자 버튼 */}
      <div className="grid grid-cols-3 gap-2">
        {buttons.flat().map((key) => (
          <button
            key={key}
            onClick={() => handleKeyPress(key)}
            className="h-12 rounded-lg bg-muted font-semibold text-lg hover:bg-accent transition-colors"
          >
            {key}
          </button>
        ))}
      </div>

      {/* 기능 버튼 */}
      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={() => handleKeyPress('clear')}
          className="h-12 rounded-lg bg-red-500/10 text-red-500 font-semibold hover:bg-red-500/20 transition-colors"
        >
          C
        </button>
        <button
          onClick={() => handleKeyPress('backspace')}
          className="h-12 rounded-lg bg-muted font-semibold hover:bg-accent transition-colors flex items-center justify-center"
        >
          <Delete className="w-5 h-5" />
        </button>
        <button
          onClick={() => handleKeyPress('enter')}
          className="h-12 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
        >
          확인
        </button>
      </div>
    </div>
  );
}

// 금액 입력용 숫자 패드
interface AmountPadProps {
  value: number;
  onChange: (value: number) => void;
  quickAmounts?: number[];
  className?: string;
}

export function AmountPad({
  value,
  onChange,
  quickAmounts = [10000, 50000, 100000],
  className,
}: AmountPadProps) {
  const handleQuickAmount = (amount: number) => {
    onChange(value + amount);
  };

  const handleClear = () => {
    onChange(0);
  };

  return (
    <div className={cn('space-y-3', className)}>
      {/* 빠른 금액 버튼 */}
      <div className="grid grid-cols-3 gap-2">
        {quickAmounts.map((amount) => (
          <button
            key={amount}
            onClick={() => handleQuickAmount(amount)}
            className="h-10 rounded-lg bg-primary/10 text-primary font-medium text-sm hover:bg-primary/20 transition-colors"
          >
            +{(amount / 10000).toFixed(0)}만
          </button>
        ))}
      </div>

      {/* 숫자 패드 */}
      <NumberPad
        value={value.toString()}
        onChange={(v) => onChange(parseInt(v, 10) || 0)}
      />

      {/* 초기화 버튼 */}
      <button
        onClick={handleClear}
        className="w-full h-10 rounded-lg bg-muted text-muted-foreground font-medium hover:bg-accent transition-colors"
      >
        금액 초기화
      </button>
    </div>
  );
}
